import { Component, h, createRef } from 'preact';
import { route } from 'preact-router';
import cx from 'classnames';
import { Subject, Subscription, fromEvent, interval } from 'rxjs';
import { distinctUntilChanged, map, debounce, filter } from 'rxjs/operators';
import findIndex from 'lodash-es/findIndex';
import { distinctUntilChangedImmutable } from 'distinct-until-changed-immutable';

import { Inquiry, InquiryListResponse, InquiryStageCounts, InquiryStage, InquiryStageDefinition } from '../../../../../types/inquiries';
import { ReqState } from '../../../../../types';
import { InquiryGridCardDragData } from 'types/ui';
import { INQUIRY_PAGE_STAGES } from '../../../../../constants/inquiries';
import { inquiryService } from '../../../../../clientside/utils/inquiry.service';
import { InquiryDetailsMobile } from '../details/inquiry-details-mobile';
import { InquiryGrid } from './inquiry-grid.component';
import { InquiryGridCard } from './inquiry-grid-card.component';
import { InquiryGridSlide } from './inquiry-grid-slide.component';
import { InquiryGridSearchModal } from '../search/inquiry-grid-search-modal';
import { InquiryPipelineFiltersUiModel } from 'inquiries/inquiry-pipeline-filters.model';
import { InquiryDocument } from 'inquiries/inquiry-document.model';

import getStore, { appState$ } from '../../../store';
import * as InquiriesActions from '../../../reducers/inquiries/inquiries.actions';
import { InquiryDetailsCardHost } from '../details/inquiry-details-card-host.component';
import { InquiryGridFiltersHost } from './inquiry-grid-filters-host.component';

interface InquiryGridPageProps {
  stage?: string; // Can be a stage slug or "search"
  s?: string; // Query param holding the current search string

  matches?: any;
}

interface InquiryGridPageState {
  // Core grid data
  stageInquiries?: { [k: string]: InquiryListResponse };
  counts?: InquiryStageCounts; // Only keep the latest counts

  // Search
  mobileSearchOpen?: boolean;
  searchInput?: string;
  searchResults?: InquiryListResponse;
  searchReqState?: ReqState;
  searchResultCount?: number;
  lastViewedStage?: string;
  
  // Card drag-and-drop support
  dragging?: boolean;
  applyCardRotation?: boolean;
  dragData?: InquiryGridCardDragData;

  // Route auto-scroll conflict prevention with mobile side-scrolling route updates
  sliderTouchCount?: number;
  
  // Slider system and responsive layout
  currentSlideIndex?: number;
  inCardMode?: boolean;

  // Selected inquiry
  selectedInquiry?: Inquiry;
  inquiryCode?: string;

  // Modals and attachments support
  visibleModal?: string;
  /** The inquiry document being edited */
  documentForEditing?: InquiryDocument;

  inquiryDetailsVisible?: boolean;
}

const HOVER_WAIT_DURATION = 1000;
const NUM_STAGES = INQUIRY_PAGE_STAGES.length;
const DEFAULT_SLIDE_INDEX = 0;
const store = getStore();

export class InquiryGridPage extends Component<InquiryGridPageProps, InquiryGridPageState> {
  moveLeftInterval: NodeJS.Timeout;
  moveRightInterval: NodeJS.Timeout;
  sliderRef = createRef();
  slideIndex$ = new Subject<number>();
  subscriptions: Subscription[] = [];

  constructor(props: InquiryGridPageProps) {
    super(props);
    console.log('INQUIRY GRID PAGE:', this.props);

    let currentSlideIndex = INQUIRY_PAGE_STAGES.findIndex(stage => stage.slug === props.stage);
    if (currentSlideIndex < 0) {
      currentSlideIndex = 0;
    }

    this.state = {
      counts: {},
      searchResultCount: 0,
      stageInquiries: {},
      inquiryDetailsVisible: false,
      currentSlideIndex,
      sliderTouchCount: 0,

      // Used as the close button target for the search modal.
      // Must not be "search"
      lastViewedStage: props.stage !== 'search' ? props.stage : 'accepted',
    };
  }

  async componentDidMount() {
    // Configure initial state
    this.setState({
      currentSlideIndex: DEFAULT_SLIDE_INDEX,
      inCardMode: this.isInCardMode()
    });
    
    // Create slide system subscriptions
    const curSlide$ = this.slideIndex$.pipe(
      distinctUntilChanged()
    );
    const debouncedIndex$ = this.slideIndex$.pipe(
      debounce(() => interval(200))
    );

    this.subscriptions.push(
      curSlide$.subscribe(currentSlideIndex => this.setState({ currentSlideIndex }))
    );
    this.subscriptions.push(
      debouncedIndex$.subscribe(index => this.setRouteToStage(INQUIRY_PAGE_STAGES[index]))
    );
    
    const resize$ = fromEvent(window, 'resize').pipe(
      map(e => e.target as Window),
      map(window => window.innerWidth),
      distinctUntilChanged()
    );
    this.subscriptions.push(
      resize$.subscribe({
        next: () => this.setState({ inCardMode: this.isInCardMode() })
      })
    );


    // Grid data subscriptions
    const inquiries$ = appState$.pipe(map(appState => appState.inquiries));
    this.subscriptions.push(
      inquiries$.pipe(
        map(inquiries => inquiries.stageInquiries)
      ).subscribe(
        stageInquiries => this.setState({ stageInquiries })
      )
    );

    this.subscriptions.push(
      inquiries$.pipe(
        map(inquiries => inquiries.counts),
        filter(counts => !!counts)
      ).subscribe(
        counts => this.setState({ counts })
      )
    );

    this.subscriptions.push(
      inquiries$.pipe(
        map(({ searchResults, searchReqState }) => ({ searchResults, searchReqState })),
        distinctUntilChangedImmutable()
      ).subscribe(({ searchResults, searchReqState }) => this.setState({ searchResults, searchReqState }))
    );

    this.loadAllStages();
  }

  /**
   * Determines whether we are in card mode or not. Used to switch between
   * on-page panels and modals for inquiry details and search.
   * 
   * When changing the criteria for this, make sure that `_grid-support.scss` is
   * also updated to match.
   */
  isInCardMode() {
    const gridSupported = CSS && CSS.supports('display: grid');
    const inSmallBreakpoint = window.innerWidth < 992; // This is media-breakpoint-down(md)

    return gridSupported && inSmallBreakpoint;
  }

  loadAllStages(filters: InquiryPipelineFiltersUiModel = {}) {
    // Retrieve inquiries for all stages. THIS IS TEMPORARY, and should be
    // replaced by a lazy-loading system similar to that used in our image
    // carousels.
    store.dispatch(InquiriesActions.loadAllStages(filters));
  }

  onCardClicked = (inquiry: Inquiry): void => {
    store.dispatch(InquiriesActions.selectInquiry(inquiry));
  }

  scrollToSlideIndex(index: number, mode?: 'instant' | 'smooth') {
    const slider = this.sliderRef.current as HTMLDivElement;
    // CSS numbering starts at 1 -- add 1
    const slide = slider.querySelector<HTMLDivElement>(`.hi-inqpage__slide:nth-child(${ index + 1 })`);

    // slide.scrollIntoView() sometimes stops with the slide partially offscreen,
    // so we have to scroll to its exact pixel offset
    if ((this.isInCardMode() && mode !== 'instant') || mode === 'smooth') {
      slider.scrollTo({
        top: 0,
        left: slide.offsetLeft,
        behavior: 'smooth'
      });
    } else {
      slider.scroll(slide.offsetLeft, 0);
    }
  }

  setRouteToStage(stage: InquiryStageDefinition) {
    route(`/kumita-brokers/inquiries/${stage.slug}`);
  }

  //////////////////////////////////////////////////////////////////////////////
  // CARD DRAG HANDLERS
  //////////////////////////////////////////////////////////////////////////////

  onRightHoverTimeout() {
    console.log('Right hover timeout!');
    const curIndex = this.state.currentSlideIndex;
    if (curIndex < NUM_STAGES - 1) {
      this.scrollToSlideIndex(curIndex + 1);
    }
  }

  onLeftHoverTimeout() {
    console.log('Left hover timeout!');
    const curIndex = this.state.currentSlideIndex;
    if (curIndex > 0) {
      this.scrollToSlideIndex(curIndex - 1);
    }
  }

  onCardDragStart(dragData: InquiryGridCardDragData) {
    // console.log('Got card drag!', dragData);
    this.setState({
      dragging: true,
      dragData,
    });

    // Let the drag overlay attach to the DOM, *then* apply the rotation.
    // This creates the illusion of the list card rotating in response to being
    // dragged.
    setTimeout(() => this.setState({ applyCardRotation: true }));
  }

  onCardDragMove(dragData: InquiryGridCardDragData) {
    // console.log('Got move!', dragData);
    this.setState({ dragData });

    if (this.isInCardMode()) {
      // Mobile card drag-to-edge-to-change-stage checking
      if (dragData.cursor.x > (window.innerWidth * .8)) {
        if (!this.moveRightInterval) {
          // Start move right timer
          this.moveRightInterval = setInterval(() => this.onRightHoverTimeout(), HOVER_WAIT_DURATION);
        }
      } else {
        if (this.moveRightInterval) {
          clearInterval(this.moveRightInterval);
          this.moveRightInterval = null;
        }
      }

      if (dragData.cursor.x < window.innerWidth * .2) {
        if (!this.moveLeftInterval) {
          this.moveLeftInterval = setInterval(() => this.onLeftHoverTimeout(), HOVER_WAIT_DURATION);
        }
      } else {
        if (this.moveLeftInterval) {
          clearInterval(this.moveLeftInterval);
          this.moveLeftInterval = null;
        }
      }
    }
  }

  onCardDragEnd() {
    this.setState({
      dragging: false,
      applyCardRotation: false
    });
    
    if (this.moveRightInterval) {
      clearInterval(this.moveRightInterval);
      this.moveRightInterval = null;
    }

    if (this.moveLeftInterval) {
      clearInterval(this.moveLeftInterval);
      this.moveLeftInterval = null;
    }
  }

  // END DRAG HANDLERS
  //////////////////////////////////////////////////////////////////////////////


  onSideScroll(event: UIEvent) {
    const element = event.srcElement as HTMLDivElement;

    // This calculation needs to be done with element.clientWidth, as we'll be
    // relying on this value in larger breakpoints where the sidenav will be
    // taking some horizontal space. 
    // 
    // window.innerWidth does not omit that horizontal space, and will cause
    // this calculation to be inaccurate.
    const currentPage = Math.floor((element.scrollLeft / element.clientWidth) + .5);
    this.slideIndex$.next(currentPage);
  }

  onSliderTouchEvent(event: TouchEvent) {
    this.setState({ sliderTouchCount: event.touches.length });
  }

  /** Infinite scroll handler */
  async onLoadTrigger(stageDefn: InquiryStageDefinition) {
    getStore().dispatch(
      InquiriesActions.loadMoreForStage(stageDefn.slug)
    );
  }

  async dispatchSearch(search?: string, filters?: InquiryPipelineFiltersUiModel) {
    // console.log('dispatchSearch:', search);
    const curSlug = this.props.stage;
    let lastViewedStage: string = this.state.lastViewedStage;
    if (curSlug !== 'search') {
      lastViewedStage = curSlug;
    }

    if (this.state.searchReqState !== ReqState.IN_PROGRESS) {
      this.setState({
        searchInput: search,
        searchReqState: ReqState.IN_PROGRESS,
        lastViewedStage
      });
  
      store.dispatch(InquiriesActions.search(search, filters)); 
    } else {
      // console.log('Suppressed double-search run.');
    }
  }

  toggleFilterCard() {
    store.dispatch(InquiriesActions.toggleFilterVisibility());
  }

  closeFilterCard() {
    store.dispatch(InquiriesActions.setFilterVisibility(false));
  }

  onFilterApply(filters: InquiryPipelineFiltersUiModel) {
    if (this.state.inCardMode) {
      this.closeFilterCard();
    }

    if (this.props.stage === 'search') {
      this.dispatchSearch(this.state.searchInput, filters);
    } else {
      this.loadAllStages(filters);
    }
  }

  goToSearchRoute(search?: string) {
    // console.log('Desktop mode search!');
    route('/kumita-brokers/inquiries/search?s=' + encodeURIComponent(search));
  }

  onIncrementCommentCount = (inquiryCode: string) => {
    // TODO Move this into redux
    const inquiries = { ...this.state.stageInquiries };
    const index = this.state.currentSlideIndex;
    const inquiry = inquiries[index].results.find(inq => inq.inquiryCode == inquiryCode)
    
    inquiry.attachments.comments += 1;  

    this.setState({
      stageInquiries: inquiries
    })
  }


  //////////////////////////////////////////////////////////////////////////////
  // UI section
  //////////////////////////////////////////////////////////////////////////////
  getSlidePager(props: InquiryGridPageProps, state: InquiryGridPageState) {
    const iconClasses: string[] = [];

    for (let i = 1; i <= NUM_STAGES; i++) {
      // CSS starts its numbering at 1
      const icon = i == (state.currentSlideIndex + 1) ? 'fas fa-circle' : 'far fa-circle';
      iconClasses.push(icon);
    }

    return (
      <div className="hi-inqpage__slide-pager">
        { iconClasses.map((classes, index) =>
          <i
            className={`${classes} hi-inqpage__slide-pager-button`}
            onClick={ () => this.scrollToSlideIndex(index) } />
        ) }
      </div>
    );
  }

  getOverlayCard(props: InquiryGridPageProps, state: InquiryGridPageState) {
    if (state.dragging) {
      const width = state.dragData.cardWidth;
      const { x, y } = state.dragData.cursor;
      const cardOffset = state.dragData.cardTouchOffset;
      const adjustedX = x - cardOffset.x;
      const adjustedY = y - cardOffset.y;

      let css = `transform: translate3d(${adjustedX}px, ${adjustedY}px, 0);`;
      css += `transform-origin: ${cardOffset.x}px ${cardOffset.y}px;`;
      css += `width: ${width}px; min-width: ${width};`;

      return (
        <div className={ cx('hi-inqpage__overlay', {
          'hi-inqpage__overlay--dragging': state.applyCardRotation
        }) } style={ css }>
          <InquiryGridCard inquiry={ state.dragData.inquiry } />
        </div>
      )
    }
  }

  getSearchDialog(props: InquiryGridPageProps, state: InquiryGridPageState) {
    if (props.stage === 'search' && state.inCardMode) {
      return (
        <InquiryGridSearchModal
          onSearch={ search => this.goToSearchRoute(search) }
          results={ state.searchResults }
          resultCount={ state.searchResultCount }
          searchInput={ state.searchInput }
          searchReqState={ state.searchReqState }
          lastViewedStage={ state.lastViewedStage } />
      );
    }
  }

  getTableModeFilterPanel(props: InquiryGridPageProps, state: InquiryGridPageState) {
    return (
      <InquiryGridFiltersHost active={ !state.inCardMode }
        onApply={ filters => this.onFilterApply(filters) }
        stageSlug={ props.stage } />
    );
  }

  getCardModeFilterPanel(props: InquiryGridPageProps, state: InquiryGridPageState) {
    return (
      <InquiryGridFiltersHost active={ state.inCardMode }
        useModal={ true }
        stageSlug={ props.stage }
        onApply={ filters => this.onFilterApply(filters) } />
    );
  }

  getGridSlider(props: InquiryGridPageProps, state: InquiryGridPageState) {
    return (
      <div className={ cx('hi-inqpage__slider', { 'd-none': props.stage === 'search' }) }>
        <div className="hi-inqpage__slides" ref={ this.sliderRef }
          onScroll={ event => this.onSideScroll(event) }
          onTouchStart={ e => this.onSliderTouchEvent(e) }
          onTouchEnd={ e => this.onSliderTouchEvent(e) }
          onTouchCancel={ e => this.onSliderTouchEvent(e) }>
          { INQUIRY_PAGE_STAGES.map((stageDefn, idx) => (
              <InquiryGridSlide stageName={ stageDefn.name }
                stageCount={ state.counts[stageDefn.slug] }
                stageActive={ idx == state.currentSlideIndex }
                cardCounts={ state.counts }
                onLeftStageClick={ () => this.scrollToSlideIndex(idx - 1) }
                onRightStageClick={ () => this.scrollToSlideIndex(idx + 1) }

                searchInput={ state.searchInput }
                onSearch={ search => this.goToSearchRoute(search) }
                onFilterButtonClick={ () => this.toggleFilterCard() }>

              <div className="hi-inqpage__grid-container">
                <InquiryGrid
                  stageSlug={ stageDefn.slug }
                  inquiries={ state.stageInquiries[stageDefn.slug] }

                  onCollectedClick={ inquiry => this.onCollectedClick(inquiry) }
                  stageFromParams={props.matches.stage}
                  onCardClicked={ this.onCardClicked }

                  onCardDragStart={ dragData => this.onCardDragStart(dragData) }
                  onCardDragMove={ dragData => this.onCardDragMove(dragData) }
                  onCardDragEnd={ () => this.onCardDragEnd() }
                  onLoadTrigger={ () => this.onLoadTrigger(stageDefn) } />

                <InquiryDetailsCardHost />
                { this.getTableModeFilterPanel(props, state) }
              </div>
            </InquiryGridSlide>
          )) }
        </div>
      </div>
    );
  }

  getSearchGrid(props: InquiryGridPageProps, state: InquiryGridPageState) {
    if (props.stage === 'search') {
      return (
        <div className="hi-inqpage__slides">
          <InquiryGridSlide stageName='search'
            stageCount={ 0 }
            stageActive={ props.stage === 'search' }
            cardCounts={ state.counts }
            searchInput={ state.searchInput }
            searchCount={ state.searchResultCount }
            onSearch={ search => this.goToSearchRoute(search) }
            onFilterButtonClick={ () => this.toggleFilterCard() }
          >
            <div className="hi-inqpage__grid-container">
              <InquiryGrid inquiries={ state.searchResults }
                stageSlug='search'
                onCardClicked={ this.onCardClicked } />

              <InquiryDetailsCardHost />
              { this.getTableModeFilterPanel(props, state) }
            </div>
          </InquiryGridSlide>
        </div>
      )
    }
  }

  onViewFtfFromViewingList(inquiry: Inquiry) {
    this.closeFilterCard();

    this.setState({
      selectedInquiry: inquiry,
      inquiryDetailsVisible: true,
      visibleModal: null
    });
  }

  async onCollectedClick(inquiry: Inquiry) {
    try {
      await inquiryService.toggleCollectionStatus(inquiry.inquiryCode);
      this.loadAllStages();
    } catch (err) {
      console.error(err);
    }
  }

  render(props: InquiryGridPageProps, state: InquiryGridPageState) {
    return (
      <div className="hi-inqpage">
        { this.getSearchDialog(props, state) }
        { state.inquiryDetailsVisible && this.isInCardMode() ? 
            <InquiryDetailsMobile>
              <InquiryDetailsCardHost />
            </InquiryDetailsMobile> : null }

        {/*
          For simplicity, the grid slider should always be attached to the DOM.
          This lets us avoid having to handle little moments of refs not
          pointing to anything between state updates.
        */}
      
        { this.getGridSlider(props, state) }
        { this.getSearchGrid(props, state) }
        { this.getSlidePager(props, state) }

        <div className={
          cx('hi-inqpage__animation-overlay', { 'd-block': state.dragging })
        }>
          { this.getOverlayCard(props, state) }
        </div>

        { this.getCardModeFilterPanel(props, state) }
        {/* { this.renderModal(props, state) } */}
      </div>
    );
  }

  componentWillReceiveProps(nextProps: InquiryGridPageProps) {
    // console.log('InquiryGridPage componentWillReceiveProps:', nextProps);
    const stage = nextProps.stage;

    const index = findIndex(INQUIRY_PAGE_STAGES, { slug: stage as InquiryStage });
    const userTouchingScreen = this.state.sliderTouchCount > 0;
    // console.log('index, touching:', index, userTouchingScreen);
    if (index >= 0 && !userTouchingScreen) {
      // If the "stage" param isn't "search" or similar, and the route update
      // did not originate from our side scroll route update system, then it
      // came from clicking a link or an initial load. If so, an instant scroll
      // to the specified stage at this point makes sense.
      this.scrollToSlideIndex(index, 'instant');
    }

    // If the stage changed, hide the details card
    if (this.props.stage !== nextProps.stage) {
      const stageDefn = INQUIRY_PAGE_STAGES.find(defn => defn.slug === nextProps.stage);
      if (stageDefn) {
        store.dispatch(InquiriesActions.selectStage(stageDefn.slug));
      }
    }

    // Check if the "s" query param changed from what we last saw. If it did,
    // fire off a search.
    // 
    // We store the last known search in our state, as loading with an "s"
    // query param means we don't get to see the query param/prop itself change.
    const params = new URLSearchParams(location.search);
    if (nextProps.stage === 'search') {
      if (params.has('s') && nextProps.s !== this.state.searchInput) {
        this.dispatchSearch(nextProps.s);
      }
    } else if (this.props.stage === 'search') {
      // If we're leaving the search state, clear the search field.
      this.setState({ searchInput: '' });
    }
  }

  componentWillUnmount() {
    this.subscriptions.forEach(sub => sub.unsubscribe());
    this.subscriptions = [];
  }
}
